export interface BranchStatistics {
  readonly leafleting: { readonly completed: number; readonly total: null };
  readonly canvassing: { readonly completed: number; readonly total: number };
  readonly history: readonly {
    readonly date: string;
    readonly streetCount: number;
    readonly electorCount: number;
  }[];
  /** Every leaflet in the branch, most scanned first. */
  readonly leafletScans: readonly LeafletScanTotal[];
}

export interface LeafletScanTotal {
  readonly id: string;
  readonly name: string;
  readonly scans: number;
}

/** Daily history covers at most the last three calendar months, through today. */
const MAXIMUM_HISTORY_DAYS = 94;

export function parseStatistics(value: unknown): BranchStatistics {
  const record = object(value);
  const leafleting = object(record.leafleting);
  const canvassing = object(record.canvassing);
  const completed = count(canvassing.completed);
  const total = count(canvassing.total);
  if (completed > total || leafleting.total !== null)
    throw new TypeError("Invalid coverage totals");
  if (!Array.isArray(record.leaflet_scans)) throw new TypeError("Invalid leaflet scans");
  const leafletScans = record.leaflet_scans.map((value: unknown) => {
    const leaflet = object(value);
    if (
      typeof leaflet.id !== "string" ||
      leaflet.id === "" ||
      typeof leaflet.name !== "string" ||
      leaflet.name === ""
    )
      throw new TypeError("Invalid leaflet scans");
    return { id: leaflet.id, name: leaflet.name, scans: count(leaflet.scans) };
  });
  if (
    leafletScans.some(
      (leaflet, index) => leaflet.scans > (leafletScans[index - 1]?.scans ?? Infinity),
    )
  )
    throw new TypeError("Leaflet scans are out of order");
  return {
    leafleting: { completed: count(leafleting.completed), total: null },
    canvassing: { completed, total },
    history: dailyHistory(record.history, (day) => ({
      streetCount: count(day.street_count),
      electorCount: count(day.elector_count),
    })),
    leafletScans,
  };
}

/** Consecutive days, each parsed by `parseDay`. */
export function dailyHistory<T>(
  value: unknown,
  parseDay: (day: Record<string, unknown>) => T,
): readonly ({ readonly date: string } & T)[] {
  if (!Array.isArray(value) || value.length === 0 || value.length > MAXIMUM_HISTORY_DAYS)
    throw new TypeError("Invalid activity history");
  let previous: number | null = null;
  return value.map((item: unknown) => {
    const day = object(item);
    if (typeof day.date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(day.date))
      throw new TypeError("Invalid activity date");
    const time = new Date(day.date).getTime();
    if (
      !Number.isFinite(time) ||
      new Date(time).toISOString().slice(0, 10) !== day.date ||
      (previous !== null && time - previous !== 86_400_000)
    )
      throw new TypeError("Invalid activity dates");
    previous = time;
    return { date: day.date, ...parseDay(day) };
  });
}

function object(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    throw new TypeError("Expected statistics object");
  return value as Record<string, unknown>;
}

export function count(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0)
    throw new TypeError("Invalid activity count");
  return value;
}
